<template>
  <div class="max-w-4xl mx-auto mt-6 min-h-8 px-4 sm:px-6 md:px-8 lg:px-10">
    <div
      v-if="$store.state.ifAuthorized"
      class="relative right-36 text-blue-500 hover:underline cursor-pointer"
      @click="goBack()"
    >
      ← Return Back
    </div>
    <div class="flex justify-center mt-2 mb-4">
      <img
        :src="require('@/assets/logo-header.png')"
        alt="Logo"
        class="h-16 sm:h-24 w-auto"
      />
    </div>
    <div v-if="isRenderLoad">
      <!-- Tracking Header -->
      <div class="flex justify-between items-center">
        <div class="flex flex-col gap-2">
          <h2 class="text-sm font-normal text-zinc-600">Tracking Number</h2>
          <h3 class="text-lg font-bold text-secondary">
            {{ applicationDetails.transactionnumber }}
          </h3>
        </div>
        <div
          class="hidden sm:block my-6 rounded-lg px-12 py-3 text-sm border"
          :class="{
            'bg-blue-100 text-blue-700 border-blue-300':
              applicationStatus === 'Assessment',
            'bg-orange-100 text-orange-700 border-orange-300':
              applicationStatus === 'Pending Documents',
            'bg-green-100 text-green-800 border-green-300':
              applicationStatus === 'Approved',
            'bg-red-100 text-red-700 border-red-300':
              applicationStatus === 'Declined',
            'bg-yellow-100 text-yellow-700 border-yellow-300':
              applicationStatus === 'Payment Preparation',
            'bg-green-100 text-green-700 border-green-300':
              applicationStatus === 'Completed',
          }"
        >
          {{ applicationStatus }}
        </div>
      </div>

      <div class="mt-6 sm:mt-0 sm:bg-transparent mb-6">
        <ul class="space-y-1 text-sm">
          <li class="flex">
            <span class="w-20 text-secondary font-semibold">Name</span>
            <span class="w-4">:</span>
            <span class="uppercase">{{ applicationDetails.membername }}</span>
          </li>
          <li class="flex">
            <span class="w-20 text-secondary font-semibold">Company</span>
            <span class="w-4">:</span>
            <span class="uppercase">{{ applicationDetails.company }}</span>
          </li>
          <li class="flex">
            <span class="w-20 text-secondary font-semibold">Provider</span>
            <span class="w-4">:</span>
            <span class="uppercase">{{ applicationDetails.providername }}</span>
          </li>
        </ul>
      </div>

      <hr />

      <!-- Desktop Tracker -->
      <div class="mt-8 mb-6 hidden sm:flex">
        <div class="flex justify-center gap-3 text-sm font-semibold w-full">
          <!-- Step 1 -->
          <div class="flex flex-col items-center text-green-600">
            <div
              class="w-8 h-8 mb-1 rounded-full bg-green-600 text-white flex items-center justify-center"
            >
              1
            </div>
            <span class="text-xs my-2 text-zinc-600">Assessment</span>
          </div>

          <div
            :class="`w-28 h-0.5 rounded-xl mt-4 ${
              applicationDetails.approvaltype === 'Approved' ||
              applicationDetails.approvaltype === 'Pending Documents'
                ? 'bg-green-600'
                : applicationDetails.approvaltype === 'Declined'
                ? 'bg-red-600'
                : 'bg-zinc-200'
            }`"
          ></div>

          <!-- Step 2 -->
          <div class="flex flex-col items-center text-gray-600">
            <div
              :class="`w-8 h-8 mb-1 rounded-full flex items-center justify-center shadow-[0_0_0_1px_rgba(0,0,0,0.05)] ${
                applicationDetails.approvaltype === 'Approved'
                  ? 'bg-green-600 text-white'
                  : applicationDetails.approvaltype === 'Declined'
                  ? 'bg-red-600 text-white'
                  : 'bg-zinc-200 text-zinc-500'
              }`"
            >
              2
            </div>
            <span class="text-xs my-2">Processing</span>
          </div>

          <div
            :class="`w-28 h-0.5 rounded-xl mt-4 ${
              applicationDetails.paymentstatus === 'Pending' ||
              applicationDetails.paymentstatus === 'Completed'
                ? 'bg-green-600'
                : 'bg-zinc-200'
            }`"
          ></div>

          <!-- Step 3 -->
          <div class="flex flex-col items-center text-gray-600">
            <div
              :class="`w-8 h-8 mb-1 rounded-full flex items-center justify-center shadow-[0_0_0_1px_rgba(0,0,0,0.05)] ${
                applicationDetails.paymentstatus === 'Pending' ||
                applicationDetails.paymentstatus === 'Completed'
                  ? 'bg-green-600 text-white'
                  : 'bg-zinc-200 text-zinc-500'
              }`"
            >
              3
            </div>
            <span class="text-xs my-2 text-center">
              Payment <br />Preparation
            </span>
          </div>

          <div
            :class="`w-28 h-0.5 rounded-xl mt-4 ${
              applicationDetails.paymentstatus === 'Completed'
                ? 'bg-green-600'
                : 'bg-zinc-200'
            }`"
          ></div>

          <!-- Step 4 -->
          <div class="flex flex-col items-center text-gray-600">
            <div
              :class="`w-8 h-8 mb-1 rounded-full flex items-center justify-center shadow-[0_0_0_1px_rgba(0,0,0,0.05)] ${
                applicationDetails.paymentstatus === 'Completed'
                  ? 'bg-green-600 text-white'
                  : 'bg-zinc-200 text-zinc-500'
              }`"
            >
              4
            </div>
            <span class="text-xs my-2">Disbursement</span>
          </div>
        </div>
      </div>

      <!-- Mobile Status Summary -->
      <div class="block sm:hidden mt-6 mb-6 text-sm text-center">
        <span
          class="inline-block rounded-full px-6 py-2 border text-xs font-semibold"
          :class="{
            'bg-blue-100 text-blue-700 border-blue-300':
              applicationStatus === 'Assessment',
            'bg-orange-100 text-orange-700 border-orange-300':
              applicationStatus === 'Pending Documents',
            'bg-green-100 text-green-800 border-green-300':
              applicationStatus === 'Approved',
            'bg-red-100 text-red-700 border-red-300':
              applicationStatus === 'Declined',
            'bg-yellow-100 text-yellow-700 border-yellow-300':
              applicationStatus === 'Payment Preparation',
            'bg-green-100 text-green-700 border-green-300':
              applicationStatus === 'Completed',
          }"
        >
          {{ applicationStatus }}
        </span>
      </div>

      <!-- Activity Log -->
      <h2 class="text-lg font-bold text-secondary">Activity Log</h2>

      <ul class="my-4 flex flex-col gap-4">
        <li
          v-for="(item, index) in applicationDetails.adminlogs"
          :key="index"
          class="bg-zinc-50 rounded-xl shadow-elegant"
        >
          <div class="pt-4 px-5">
            <h2 class="text-secondary text-lg font-bold">
              {{ descriptionTitle(item.status) }}
            </h2>
            <span class="text-zinc-600 text-xs">
              {{ dateFormatHandler(item.createdAt) }}
            </span>
            <p
              class="text-sm text-zinc-500 pt-3 py-4"
              v-html="descriptionComposition(item.status, item)"
            ></p>
          </div>
        </li>
      </ul>
    </div>
    <div v-else class="flex justify-center">
      <div class="loader relative w-12 h-12 rounded-full animate-rotate">
        <div
          class="absolute inset-0 rounded-full border-[5px] border-white animate-prixClipFix"
        ></div>
        <div
          class="absolute inset-2 rounded-full border-[5px] border-[#FF3D00] animate-prixClipFix transform rotate-[180deg]"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { inject, onMounted, ref, computed } from "vue";
import { useRoute } from "vue-router";
import CryptoJS from "crypto-js";

export default {
  setup() {
    const axios = inject("$axios");
    const router = inject("$router");
    const moment = inject("$moment");
    const route = useRoute();
    const trackingId = ref();
    const applicationDetails = ref([]);
    const isRenderLoad = ref(false);

    onMounted(() => {
      trackingId.value = route.params.id;
      checkIfValidHandler();
    });
    const checkIfValidHandler = async () => {
      try {
        const { data } = await axios.get("/public/tracking", {
          params: { search: trackingId.value },
        });
        console.log(!data.result);
        if (!data.result) {
          router.push(`/`);
        } else {
          informationDetails();
        }
      } catch (error) {
        console.log(error);
      }
    };
    const informationDetails = async () => {
      try {
        const { data } = await axios.get("public/tracking/application", {
          params: { search: trackingId.value },
        });

        applicationDetails.value = data;
        setTimeout(() => {
          isRenderLoad.value = true;
        }, 1000);
      } catch (error) {
        console.log(error);
      }
    };
    const dateFormatHandler = (val) => {
      return moment(val).format("LLL");
    };
    const descriptionComposition = (val) => {
      let EndDate = moment(applicationDetails.value.updatedAt).add(24, "hours");
      let now = moment();

      let amount = applicationDetails.value.reimbursableamount
        ? applicationDetails.value.reimbursableamount
        : 0;
      let GuaranteedAmount = `₱${amount.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
      })}`;

      let renderStart = applicationDetails.value.renderstartdate;
      let renderDays = applicationDetails.value.graceperiod;
      let renderEnd;
      renderEnd = moment(renderStart).add(renderDays, "days").format("LL");

      console.log(renderStart);
      let isValid = now.isSameOrBefore(EndDate);

      let encryptReference = CryptoJS.AES.encrypt(
        applicationDetails.value.paymentreference,
        process.env.VUE_APP_ENCRYPT_SECRET_KEY
      ).toString();

      let url = `${process.env.VUE_APP_CLIENT_URL}/#/temporary/meta/x/file/${encryptReference}`;

      let urlDeclined = `${process.env.VUE_APP_CLIENT_URL}/#/temporary/meta/x/decline/${applicationDetails.value.transactionnumber}`;

      let ApplicationStatus = val;
      let ApplicationComposition;

      switch (ApplicationStatus) {
        case "New Application":
          ApplicationComposition =
            "We have already received and will review your reimbursement application soon.";
          break;

        case "Assessment":
          ApplicationComposition =
            "Your reimbursement application is being reviewed.";
          break;

        case "Pending Documents":
          let items = applicationDetails.value.AttachmentFiles || [];
          let requiredDocs = `
  <p class='mt-2 mb-1 font-semibold'>Pending Document(s):</p>
  <ul class='list-disc list-inside'>
    ${items.map((item) => `<li>${item.particular}</li>`).join("")}
  </ul>
`;

          ApplicationComposition = !renderStart
            ? `Your reimbursement application was previously reviewed and found to have incomplete supporting documents. If you have already submitted the required documents, no further action is needed. Otherwise, please ensure to submit the following:<br>${requiredDocs}`
            : `Your reimbursement application has been reviewed and found to have incomplete supporting document(s). Please submit the required documents by <span class="text-stone-900 font-bold">${renderEnd}</span> to avoid disapproval.<br>${requiredDocs}`;
          break;
        case "Approved":
          ApplicationComposition = `The evaluation process for your reimbursement application is complete and has been approved for the amount of ${GuaranteedAmount}. Payment preparation is now in progress.`;
          break;

        case "Payment Preparation":
          ApplicationComposition = `Your application has been submitted for payment and will be processed shortly.`;
          break;

        case "Payment Released":
          ApplicationComposition = isValid
            ? `Your application has been completed, and the payment has been deposited to your account. Please click <a href="${url}" target="_blank"><span class="text-blue-800 font-bold">here</span></a> to view the proof of payment (valid for 24 hours)`
            : `Your application has been completed, and the payment has been deposited to your account.`;
          break;

        default:
          ApplicationComposition = `We regret to inform you that your reimbursement application has been declined. Kindly <a href="${urlDeclined}" target="_blank"><span class="text-blue-800 font-bold"> click here</span></a> to review the details. `;
          break;
      }

      return ApplicationComposition;
    };

    const descriptionTitle = (val) => {
      let ApplicationStatus = val;
      let ApplicationTitle;

      switch (ApplicationStatus) {
        case "New Application":
          ApplicationTitle = "Application Request Received";
          break;

        case "Assessment":
          ApplicationTitle = "For Assessment";
          break;

        case "Pending Documents":
          ApplicationTitle = "For Completion of Documents";
          break;

        case "Approved":
          ApplicationTitle = "Approval Granted";
          break;

        case "Payment Preparation":
          ApplicationTitle = "Payment Preparation";
          break;

        case "Payment Released":
          ApplicationTitle = "Payment Released";
          break;

        default:
          ApplicationTitle = "Declined";
          break;
      }

      return ApplicationTitle;
    };

    const applicationStatus = computed(() => {
      let status;
      status = applicationDetails.value.paymentstatus
        ? applicationDetails.value.paymentstatus
        : applicationDetails.value.approvaltype;

      return status === "Pending" ? "Payment Preparation" : status;
    });
    const goBack = () => {
      router.back();
    };
    return {
      trackingId,
      checkIfValidHandler,
      informationDetails,
      applicationDetails,
      dateFormatHandler,
      descriptionComposition,
      descriptionTitle,
      applicationStatus,
      isRenderLoad,
      goBack,
    };
  },
};
</script>
<style scoped>
@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes prixClipFix {
  0% {
    clip-path: polygon(50% 50%, 0 0, 0 0, 0 0, 0 0, 0 0);
  }
  50% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 0, 100% 0, 100% 0);
  }
  75%,
  100% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 100% 100%, 100% 100%);
  }
}

.loader {
  animation: rotate 1s linear infinite;
}
.animate-rotate {
  animation: rotate 1s linear infinite;
}
.animate-prixClipFix {
  animation: prixClipFix 2s linear infinite;
}
</style>
